import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Plus, MessageCircle, Trash2, X, Save, Edit3 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: '', email: '', phone: '', company: '', notes: '' };

export default function ClientsManagement() {
  const { getAuthHeader } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null=create, id=edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/clients`, { headers: getAuthHeader(), withCredentials: true });
      setClients(data);
    } catch (e) { logger.error('Failed to load clients:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setForm({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      notes: client.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/clients/${editingId}`, form,
          { headers: getAuthHeader(), withCredentials: true });
      } else {
        await axios.post(`${API}/admin/clients`, form,
          { headers: getAuthHeader(), withCredentials: true });
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente remover este cliente?')) return;
    try {
      await axios.delete(`${API}/admin/clients/${id}`, { headers: getAuthHeader(), withCredentials: true });
      load();
    } catch (e) { logger.error('Failed to delete client:', e); }
  };

  const openWhatsApp = (phone, name) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Olá ${name}!`);
    window.open(`https://wa.me/55${cleanPhone}?text=${message}`, '_blank');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-[#22D3EE]" size={40} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Gestão de Clientes</h1>
          <p className="text-[#94A3B8]">{clients.length} clientes cadastrados • Clique no nome para ver detalhes</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="add-client-button"
          className="flex items-center gap-2 bg-[#22D3EE] text-[#080710] px-5 py-2.5 rounded-lg font-semibold hover:shadow-[0_0_20px_rgba(34,211,238,0.5)] transition-all"
        >
          <Plus size={18} /> Novo Cliente
        </button>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
            data-testid="client-form-modal"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-[#0d0f17] border border-[#22D3EE]/30 rounded-2xl p-6 w-full max-w-md my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-[#F8FAFC]">
                  {editingId ? 'Editar Cliente' : 'Novo Cliente'}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-[#94A3B8] hover:text-[#F8FAFC]"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  data-testid="client-name-input"
                  type="text" required placeholder="Nome*"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <input
                  data-testid="client-email-input"
                  type="email" required placeholder="Email*"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <input
                  data-testid="client-phone-input"
                  type="tel" required placeholder="Telefone*"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <input
                  type="text" placeholder="Empresa"
                  value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                />
                <textarea
                  placeholder="Observações"
                  rows={4}
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="client-notes-input"
                  className="w-full bg-[#080710] border border-white/10 rounded-lg px-4 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none resize-none"
                />
                <button
                  type="submit" disabled={saving}
                  data-testid="save-client-button"
                  className="w-full bg-[#22D3EE] text-[#080710] py-2.5 rounded-lg font-semibold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                  {saving ? 'Salvando...' : (editingId ? 'Salvar Alterações' : 'Criar Cliente')}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {clients.length === 0 ? (
        <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-12 text-center">
          <p className="text-[#94A3B8]">Nenhum cliente cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-card-${c.id}`}
              className="bg-[#0d0f17] border border-white/5 hover:border-[#22D3EE]/30 rounded-xl p-5 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#F8FAFC] truncate">{c.name}</h3>
                  {c.company && <p className="text-xs text-[#94A3B8] mt-0.5">{c.company}</p>}
                </div>
              </div>
              <div className="space-y-1 mb-3 text-sm">
                <p className="text-[#94A3B8] truncate">📧 {c.email}</p>
                <p className="text-[#94A3B8]">📞 {c.phone}</p>
              </div>
              {c.notes && (
                <div className="bg-black/20 border border-white/5 rounded-lg p-3 mb-3 max-h-24 overflow-y-auto">
                  <p className="text-xs text-[#94A3B8] mb-1">Observações</p>
                  <p className="text-xs text-[#F8FAFC] whitespace-pre-wrap">{c.notes}</p>
                </div>
              )}
              <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                <button
                  onClick={() => openEdit(c)}
                  data-testid={`edit-client-${c.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 p-2 rounded bg-[#22D3EE]/10 hover:bg-[#22D3EE]/20 text-[#22D3EE] text-xs font-semibold transition-colors"
                >
                  <Edit3 size={14} /> Editar
                </button>
                <button
                  onClick={() => openWhatsApp(c.phone, c.name)}
                  data-testid={`whatsapp-client-${c.id}`}
                  className="p-2 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 transition-colors"
                  title="WhatsApp"
                >
                  <MessageCircle size={14} />
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  data-testid={`delete-client-${c.id}`}
                  className="p-2 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                  title="Excluir"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
